import React from 'react';
import { REVIEW_EDGE_COLORS } from '../../canvas/edgeStyles';
import { badgeClass } from '../../canvas/nodes/nodeStyles';
import { ReviewerEdgeKind, ReviewerStatus } from '../graphTypes';

/**
 * Presentation shared by the four reviewer nodes. Everything structural
 * (shell, header, body, handles, badges) comes from the pipeline canvas'
 * `nodeStyles.ts` — the reviewer graph is the same canvas, so it must not
 * invent a second look. Only the three things the reviewer adds live here: the
 * status glyph, the left-border accent, and the dimming of what is not built.
 */

const GLYPHS: Record<ReviewerStatus, string> = {
  on: '●',
  off: '○',
  unavailable: '⊘',
};

const GLYPH_CLASS: Record<ReviewerStatus, string> = {
  on: 'text-[var(--accent-strong)]',
  off: 'text-[var(--app-text-muted)]',
  unavailable: 'text-[var(--border-strong)]',
};

const GLYPH_TITLE: Record<ReviewerStatus, string> = {
  on: 'switched on',
  off: 'switched off — it can be turned on',
  unavailable: 'not built yet',
};

/**
 * One character, and the only status signal a lean node carries.
 *
 * `label` is what the glyph is a status FOR — the source, tier or policy's own
 * label ("reference images", "pair", "regenerate the candidate") — so the
 * accessible name says what is on/off/unavailable, not just the bare state
 * word a screen reader would otherwise read out of context.
 */
export const StatusGlyph: React.FC<{ status: ReviewerStatus; label: string; className?: string }> = ({
  status,
  label,
  className = '',
}) => (
  <span
    role="img"
    aria-label={`${label}: ${GLYPH_TITLE[status]}`}
    title={GLYPH_TITLE[status]}
    className={`text-[10px] leading-none ${GLYPH_CLASS[status]} ${className}`}
  >
    {GLYPHS[status]}
  </span>
);

/** The three-state switch every source, tier and policy reports. */
export const reviewerStatus = (on: boolean, available: boolean): ReviewerStatus => {
  if (!available) return 'unavailable';
  return on ? 'on' : 'off';
};

/**
 * A pin React Flow positions itself (edge of the node, vertically centred) —
 * unlike the canvas' `handleClass`, which is `!static` because it sits inside a
 * flowed row. Used for the single-pin nodes; the core's per-slot ports use the
 * canvas' row pins.
 */
export const reviewHandleClass =
  '!h-2 !w-2 !min-h-0 !min-w-0 !border !border-[var(--border-strong)] !bg-[var(--accent-strong)]';

/** A node that is not built yet reads as a ghost of itself, never as an error. */
export const statusOpacity = (status: ReviewerStatus): string =>
  status === 'unavailable' ? 'opacity-40' : '';

/** The 3px left border every reviewer node wears, in its flow's colour. */
export const accentBorder = (kind: ReviewerEdgeKind): React.CSSProperties => ({
  borderLeft: `3px solid ${REVIEW_EDGE_COLORS[kind]}`,
});

/** `fired` / `skipped` from a run's own record — amber, the attention colour. */
export const outcomeBadgeClass = `${badgeClass} border-amber-400/40 bg-amber-500/10 text-amber-200`;

/** A run's tier outcome badge: pass reads done, anything else reads attention. */
export const traceOutcomeBadgeClass = (outcome: string): string =>
  outcome === 'pass'
    ? `${badgeClass} border-emerald-400/40 bg-emerald-500/10 text-emerald-200`
    : `${badgeClass} border-amber-400/40 bg-amber-500/10 text-amber-200`;
